import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { giornoLungo, oggi, piuMesi } from "@/lib/crm";
import { cn } from "@/lib/utils";

const MOTIVI = ["Prezzo", "Ha scelto un altro negozio", "Tempi di consegna", "Ha rimandato il progetto", "Non risponde più", "Altro"];

/** Il giorno in cui riprenderlo: fra tre mesi da oggi. */
export const fraTreMesi = () => piuMesi(oggi(), 3);

/**
 * «Perché abbiamo perso …?»: il motivo a un tocco, una nota se serve (con «Altro» è lei il motivo), e la
 * ripresa fra tre mesi, che è la prossima azione del cliente con quella data: nessun promemoria nuovo
 * (decisione del 30-10). Il gesto lo fa chi apre il dialogo: `onConferma(motivo, riprendi)`.
 * `prefisso` dà i testid: «kanban-perso» nel Kanban, «perso» nella scheda.
 */
export default function PersoDialog({ nome, open, onOpenChange, onConferma, verbo = "Segna perso", prefisso = "perso", busy = false }) {
  const [scelto, setScelto] = useState(null);
  const [nota, setNota] = useState("");
  const [riprendi, setRiprendi] = useState(false);
  useEffect(() => {
    if (open) {
      setScelto(null);
      setNota("");
      setRiprendi(false);
    }
  }, [open]);
  const motivo = [scelto && scelto !== "Altro" ? scelto : null, nota.trim() || null].filter(Boolean).join(": ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${prefisso}-modal`} className="max-h-[90dvh] overflow-y-auto sm:max-w-[560px]">
        <form
          className="grid gap-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (motivo) onConferma(motivo, riprendi);
          }}
        >
          <DialogHeader>
            <DialogTitle>Perché abbiamo perso {nome}?</DialogTitle>
            <DialogDescription>Serve a capire dove si perde di più. Un tocco basta.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-inside min-[420px]:grid-cols-2" role="group" aria-label="Il motivo">
            {MOTIVI.map((m, i) => (
              <button
                key={m}
                type="button"
                aria-pressed={scelto === m}
                onClick={() => setScelto(scelto === m ? null : m)}
                data-testid={`${prefisso}-scelta-${i}`}
                className={cn(
                  "min-h-12 rounded-sm border px-3 py-2 text-left font-semibold transition-colors duration-150 ease-enter",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                  scelto === m ? "border-brand bg-brand-subtle text-brand-ink" : "border-line bg-surface text-ink hover:bg-surface-2",
                )}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="grid gap-inside">
            <Label htmlFor={`${prefisso}-motivo`}>
              {scelto === "Altro" ? "Cosa è successo" : "Una nota"}{" "}
              <span className="font-normal text-ink-3">
                · {scelto && scelto !== "Altro" ? "facoltativa" : "scrivila se non c'è il motivo"}
              </span>
            </Label>
            <Input
              id={`${prefisso}-motivo`}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              maxLength={500}
              placeholder="Per esempio: altro negozio a Lanciano, circa 3.000 € in meno"
              data-testid={`${prefisso}-motivo`}
            />
          </div>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id={`${prefisso}-riprendi`}
              checked={riprendi}
              onChange={(e) => setRiprendi(e.target.checked)}
              className="mt-0.5 size-5 shrink-0 accent-brand"
              data-testid={`${prefisso}-riprendi`}
            />
            <Label htmlFor={`${prefisso}-riprendi`} className="grid gap-micro font-normal">
              <span className="font-semibold text-ink">Da riprendere fra 3 mesi</span>
              <span className="text-small text-ink-2">
                Diventa la prossima azione di {giornoLungo(fraTreMesi())}: la ritrovi nella scheda e nell'elenco.
              </span>
            </Label>
          </div>
          <DialogFooter className="gap-inside">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={busy || !motivo} data-testid={`${prefisso}-salva`}>
              {busy ? "Salvo…" : verbo}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
